import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    UserPlusIcon,
    BuildingOfficeIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    ShieldCheckIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const API_URL = '/api/v1';

export default function ParticipantManagement() {
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        accountName: '',
        accountType: 'GRID_OPERATOR',
        email: '',
        phone: '',
        countryCode: 'MA',
        legalRep: ''
    });

    const fetchParticipants = async () => {
        try {
            const res = await axios.get(`${API_URL}/accounts`);
            setParticipants(res.data);
        } catch (err) {
            console.error('Error fetching participants:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParticipants();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                account_name: formData.accountName,
                account_type: formData.accountType,
                email: formData.email,
                phone: formData.phone,
                country_code: formData.countryCode,
                legal_representative: formData.legalRep,
                registry_code: 'MAPGO',
                account_number: `MA-${formData.accountType.substring(0, 3)}-${Math.floor(Math.random() * 1000)}`,
                standard_terms_accepted: true,
                status: 'ACTIVE' 
            };
            await axios.post(`${API_URL}/accounts`, payload);
            setShowModal(false);

            setFormData({
                accountName: '',
                accountType: 'GRID_OPERATOR',
                email: '',
                phone: '',
                countryCode: 'MA',
                legalRep: ''
            });
            fetchParticipants();
        } catch (err) {
            alert('Failed to provision institution: ' + (err.response?.data?.detail || err.message));
        }
    };

    return (
        <div className="space-y-6 sm:space-y-8 animate-fade-in px-2 sm:px-4 md:px-0">
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-6">
                <div className="text-center md:text-left">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                        Institutional Governance
                    </h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                        Sovereign Node Provisioning (ONEE / VVB / DSO)
                    </p>
                </div>
                
                <button
                    onClick={() => setShowModal(true)}
                    className="w-full md:w-auto bg-[#155e75] hover:bg-[#0e4e61] text-white px-6 py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#155e75]/10 hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                    <UserPlusIcon className="w-5 h-5" /> Provision New Institution
                </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {participants
                    .filter(p => !['PRODUCER', 'TRADER'].includes(p.account_type))
                    .map(p => (
                        <div 
                            key={p.id} 
                            className="bg-white p-5 rounded-2xl border border-[#adc6ce]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-[#155e75] transition-all duration-300 shadow-sm"
                        >
                            <div className="flex items-start sm:items-center gap-4">
                                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center border border-slate-100 flex-shrink-0">
                                    {p.account_type === 'GRID_OPERATOR' ? (
                                        <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
                                    ) : (
                                        <ShieldCheckIcon className="w-6 h-6 text-emerald-600" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">
                                        {p.account_name}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                        <span className="text-[9px] font-bold text-[#155e75] uppercase px-2.5 py-0.5 bg-blue-50 border border-blue-100 rounded-full">
                                            {p.account_type}
                                        </span>
                                        <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">
                                            {p.account_number}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center justify-between sm:justify-end gap-6 pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-50">
                                <div className="text-left sm:text-right">
                                    <p className="text-[10px] font-bold text-emerald-600 uppercase flex items-center gap-1 sm:justify-end">
                                        <CheckCircleIcon className="w-3.5 h-3.5" /> Node Active
                                    </p>
                                    <p className="text-[10px] text-slate-400 font-semibold break-all mt-0.5">
                                        {p.email}
                                    </p>
                                </div>
                                <ChevronRightIcon className="w-5 h-5 text-slate-300 hidden sm:block" />
                            </div>
                        </div>
                    ))}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white max-w-xl w-full p-6 sm:p-8 md:p-10 border border-[#adc6ce]/40 shadow-2xl animate-slide-up rounded-2xl max-h-[90vh] overflow-y-auto relative">
                        
                        <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
                            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 uppercase tracking-tight">
                                Provision Institution
                            </h2>
                            <button 
                                onClick={() => setShowModal(false)}
                                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg bg-slate-50"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleCreate} className="space-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">
                                    Institution Legal Name
                                </label>
                                <input 
                                    required 
                                    placeholder="e.g. Lydec Casablanca" 
                                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-[#155e75] focus:bg-white text-xs font-bold transition-all" 
                                    value={formData.accountName} 
                                    onChange={e => setFormData({ ...formData, accountName: e.target.value })} 
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">
                                        Authoritative Role
                                    </label>
                                    <select 
                                        className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-[#155e75] focus:bg-white text-xs font-bold uppercase" 
                                        value={formData.accountType} 
                                        onChange={e => setFormData({ ...formData, accountType: e.target.value })}
                                    >
                                        <option value="GRID_OPERATOR">Grid Operator (DSO)</option>
                                        <option value="VVB_AUDITOR">Independent Auditor (VVB)</option>
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">
                                        Jurisdiction
                                    </label>
                                    <input 
                                        value="Morocco (MA)" 
                                        disabled 
                                        className="w-full p-3.5 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-400" 
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block pl-1">
                                    Master Admin Email
                                </label>
                                <input 
                                    type="email" 
                                    required 
                                    placeholder="admin@institution.ma" 
                                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-[#155e75] focus:bg-white text-xs font-bold transition-all" 
                                    value={formData.email} 
                                    onChange={e => setFormData({ ...formData, email: e.target.value })} 
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)} 
                                    className="w-full sm:flex-1 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="w-full sm:flex-1 bg-[#155e75] hover:bg-[#0e4e61] text-white py-3.5 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-[#155e75]/10 transition-all text-center"
                                >
                                    Execute Provisioning
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}